import { useCallback, useEffect, useState } from 'react'
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'

import { fetchCurrentUserDetailsIfNeeded } from '@/actions/userActions'
import { postCompleteOnboarding } from '@/features/user/dashboard/user-action-items/lib/userActionItems.slice'
import { fetchUserOnboardingStepsIfNeeded } from '@/features/onboarding/user-onboarding-steps/onboarding.actions'
import { getCurrentUser } from '@/selectors/user.selectors'
import { useReselector, useScrollRef } from '@/utils/sharedHooks'
import { SetupLanding } from './setup-landing/SetupLanding'
import { ConnectStage } from './connect-stage'
import { AccountsStage } from './accounts-stage'
import { UploadStatementsStage } from './upload-statements-stage'
import { IncompleteAccount } from './connect-stage/IncompleteAccount'
import { TransactionReview } from './transaction-review'
import { BooksQuestion } from './books-question'
import {
  SeparationSkipScreen,
  SKIP_SEPARATION_KEY,
  skipSeparation,
} from './separation'
import { getIsFetching } from '@/reducers/fetch'
import { Banner, BannerVariants } from '@/ui'
import { AccountType } from './account-type'
import { BankingApplicationStage } from './banking-application'
import { BankingIntroPage } from '@/features/banking/BankingIntroPage'
import { CONNECT_BANK_PATHS } from '@/features/banking/banking.constants'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '@/flags'
import {
  ONBOARDING_BANKING_INTRO_PATH,
  ONBOARDING_BANKING_PATH,
  ONBOARDING_LANDING_PATH,
} from '@/components/routes/onboardingRedirect'

const ONBOARDING_TRANSACTION_REVIEW_PATH =
  '/onboarding-v2?stage=transaction-review'

const NON_REDIRECT_SCREENS_V2 = [
  /onboarding-v2\?stage=connect&step=link/, // Link accounts
  /onboarding-v2\?stage=banking-intro/, // Banking intro
  /onboarding-v2\?stage=banking/, // Banking application
]

/**
 * The separation stages a user may resume onto, added to the allowlist when the flag is on.
 *
 * `account-type` is deliberately absent: it renders fixture institutions and persists
 * nothing, so leaving it here let a user reach it and lose their answers. Restore it with
 * the runtime wiring in CORE-7540.
 */
const SEPARATION_RESUMABLE_SCREENS = [
  /onboarding-v2\?stage=landing/, // Onboarding intro/overview
  /onboarding-v2\?stage=transaction-review/, // Transaction review
  /onboarding-v2\?stage=transaction-review-preview/, // Transaction review preview (standalone)
  /onboarding-v2\?stage=books-question/, // Books question
]

const SEPARATION_STAGES = [
  'account-type',
  'transaction-review',
  'transaction-review-preview',
  'books-question',
]

export const OnboardingV2 = () => {
  const { pathname, search, state } = useLocation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const stage = searchParams.get('stage') || ''
  const currentUser = useReselector(getCurrentUser)
  const { scrollRef, scrollToRef } = useScrollRef({ autoScroll: true })

  const presortEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.presortEnabled,
    false
  )

  const fromGettingStarted = state?.fromGettingStarted || false

  const urlPath = pathname + search
  const lastOnboardingScreen = currentUser?.lastOnboardingScreen?.replace(
    /\\/g,
    '/'
  )

  useEffect(() => {
    dispatch(fetchCurrentUserDetailsIfNeeded(true))
  }, [dispatch])

  useEffect(() => {
    if (!presortEnabled) return
    scrollToRef()
  }, [presortEnabled, scrollToRef, stage])

  useEffect(() => {
    if (fromGettingStarted) {
      if (urlPath !== '/onboarding-v2?stage=statements&step=noLbaIntro') {
        navigate('/onboarding-v2?stage=statements&step=noLbaIntro')
      }
      return
    }

    const allowedScreens = presortEnabled
      ? [...NON_REDIRECT_SCREENS_V2, ...SEPARATION_RESUMABLE_SCREENS]
      : NON_REDIRECT_SCREENS_V2

    const isUrlNonRedirectScreen = allowedScreens.some((screen) =>
      screen.test(urlPath)
    )
    const isLastScreenNonRedirect = lastOnboardingScreen
      ? allowedScreens.some((screen) => screen.test(lastOnboardingScreen))
      : false

    if (isUrlNonRedirectScreen) {
      return
    }

    if (lastOnboardingScreen && isLastScreenNonRedirect) {
      navigate(lastOnboardingScreen)
      return
    }

    navigate('/onboarding-v2')
    return
  }, [
    navigate,
    urlPath,
    lastOnboardingScreen,
    fromGettingStarted,
    presortEnabled,
  ])

  // Sorting is the last thing onboarding asks for, so this is where it is stamped complete.
  // Navigating on a failed stamp bounces the member back here with their summary gone.
  const finishSorting = useCallback(async () => {
    const completed = await dispatch(postCompleteOnboarding())
    if (!completed) return false
    // Completion seeds the getting-started steps, and the dashboard reuses any it has cached.
    await dispatch(
      fetchUserOnboardingStepsIfNeeded({ alwaysFetch: true, expanded: true })
    )
    navigate('/dashboard')
    return true
  }, [dispatch, navigate])

  const isSkipping = useReselector(getIsFetching, SKIP_SEPARATION_KEY)
  const [skipFailed, setSkipFailed] = useState(false)
  useEffect(() => setSkipFailed(false), [stage])

  // Released before onboarding is stamped: a failed release leaves the member here to retry,
  // where one after the stamp would strand their untouched rows.
  const skipSorting = useCallback(
    async (screen: SeparationSkipScreen, openQuestions: number) => {
      if (isSkipping) return
      setSkipFailed(false)
      const released = await dispatch(skipSeparation({ screen, openQuestions }))
      const skipped = released ? await finishSorting() : false
      if (!skipped) {
        setSkipFailed(true)
        scrollToRef()
      }
    },
    [dispatch, finishSorting, isSkipping, scrollToRef]
  )
  const onSkipSorting = (screen: SeparationSkipScreen, openQuestions: number) =>
    void skipSorting(screen, openQuestions)

  const renderStage = () => {
    // A separation stage reached with the flag off would otherwise render nothing. Stays
    // inside onboarding: Router sends any path outside it back here while
    // `onboardingItemsCompletedAt` is null, so /dashboard would bounce straight back.
    if (!presortEnabled && SEPARATION_STAGES.includes(stage)) {
      return <Navigate to={ONBOARDING_LANDING_PATH} replace />
    }

    switch (stage) {
      case 'landing':
        return <SetupLanding />
      case 'banking-intro':
        return (
          <BankingIntroPage
            onBack={() => navigate(ONBOARDING_LANDING_PATH)}
            onContinue={() => navigate(ONBOARDING_BANKING_PATH)}
            onSkip={() => navigate(CONNECT_BANK_PATHS.onboarding)}
          />
        )
      case 'banking':
        return (
          <BankingApplicationStage
            onBack={() => navigate(ONBOARDING_BANKING_INTRO_PATH)}
            onContinue={() => navigate(CONNECT_BANK_PATHS.onboarding)}
            onSkip={() => navigate(CONNECT_BANK_PATHS.onboarding)}
          />
        )
      case 'connect':
        return (
          <ConnectStage
            onBack={() => navigate(ONBOARDING_BANKING_INTRO_PATH)}
          />
        )
      case 'account-type':
        return (
          <AccountType
            onBack={() => navigate(CONNECT_BANK_PATHS.onboarding)}
            onContinue={() => navigate(ONBOARDING_TRANSACTION_REVIEW_PATH)}
          />
        )
      case 'transaction-review':
        return (
          <TransactionReview
            onBack={() => navigate(CONNECT_BANK_PATHS.onboarding)}
            onContinue={() => navigate('/onboarding-v2?stage=books-question')}
            onSkip={onSkipSorting}
          />
        )
      case 'transaction-review-preview':
        return (
          <TransactionReview
            onBack={() => navigate(-1)}
            onContinue={() => navigate('/onboarding-v2?stage=books-question')}
            onSkip={onSkipSorting}
          />
        )
      case 'books-question':
        return (
          <BooksQuestion
            onBack={() => navigate(ONBOARDING_TRANSACTION_REVIEW_PATH)}
            onContinue={() => void finishSorting()}
            onSkip={onSkipSorting}
          />
        )
      case 'account':
        return <AccountsStage />
      case 'statements':
        return <UploadStatementsStage />
      case 'incomplete':
        return <IncompleteAccount />
      default:
        // An unknown stage and the no-stage entry both land here rather than on a blank
        // page with no way out.
        return <SetupLanding />
    }
  }

  return (
    <>
      {/* Layout's <main> is the scroll container and does not unmount between stages, so
          nothing else resets its position. This anchor is what the stage change scrolls to. */}
      <span ref={scrollRef} />
      {skipFailed && (
        <Banner
          variant={BannerVariants.DANGER}
          wrapperClassName="mx-auto mt-6 w-full max-w-3xl px-6"
        >
          We couldn&apos;t skip sorting just now. Nothing is lost, so try Skip
          again.
        </Banner>
      )}
      {renderStage()}
    </>
  )
}
